import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Clock, ArrowRight, Brain, CalendarClock } from 'lucide-react';
import { fireSuccessConfetti } from '@/lib/confetti';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Task, TimerMode } from '@/types/task';

interface SessionCompleteDialogProps {
  isOpen: boolean;
  task: Task | null;
  timerMode: TimerMode | null;
  onComplete: (needsMoreTime: boolean) => void;
}

export function SessionCompleteDialog({ isOpen, task, timerMode, onComplete }: SessionCompleteDialogProps) {
  const [countdown, setCountdown] = useState(10);

  // Countdown timer for grace period
  useEffect(() => {
    if (!isOpen) {
      setCountdown(10);
      return;
    }

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen]);

  // Play chime and confetti when dialog opens
  useEffect(() => {
    if (isOpen) {
      playChime();
      fireSuccessConfetti();
    }
  }, [isOpen]);

  const playChime = () => {
    // Create a soft, pleasant chime using Web Audio API
    try {
      const AudioContextCtor = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      const audioContext = AudioContextCtor ? new AudioContextCtor() : null;
      if (!audioContext) return;
      
      const playTone = (frequency: number, startTime: number, duration: number, volume: number) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        // Soft attack and decay
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };

      // Play a gentle three-tone chime (C5, E5, G5)
      const now = audioContext.currentTime;
      playTone(523.25, now, 0.8, 0.15); // C5
      playTone(659.25, now + 0.15, 0.8, 0.12); // E5
      playTone(783.99, now + 0.3, 1.0, 0.1); // G5
    } catch {
      // Audio not available (e.g. autoplay policy)
    }
  };

  if (!isOpen) return null;

  // Mode-specific messaging
  const getModeMessage = () => {
    switch (timerMode) {
      case 'manager':
        return {
          icon: CalendarClock,
          title: 'Time Block Complete!',
          subtitle: 'Great discipline.',
          question: 'Need more time for this task?',
          doneLabel: 'All done!',
          moreLabel: 'Need more time',
        };
      default:
        return {
          icon: CheckCircle,
          title: 'Session Complete!',
          subtitle: 'Great focus on:',
          question: 'Do you need more time for this task?',
          doneLabel: 'All done!',
          moreLabel: 'Need more time',
        };
    }
  };

  const modeMessage = getModeMessage();
  const IconComponent = modeMessage.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-background"
      >
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute inset-0 bg-gradient-to-br from-success/10 via-background to-primary/10 opacity-50"
            animate={{
              scale: [1, 1.02, 1],
              opacity: [0.5, 0.6, 0.5],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </div>

        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative z-10 flex flex-col items-center gap-8 max-w-md mx-auto px-6 text-center"
        >
          {/* Success icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", damping: 15 }}
            className="p-4 rounded-full bg-success/10"
          >
            <IconComponent className="h-16 w-16 text-success" />
          </motion.div>

          {/* Title */}
          <div className="space-y-2">
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-3xl font-bold"
            >
              {modeMessage.title}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-muted-foreground"
            >
              {modeMessage.subtitle}
            </motion.p>
            {task && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-lg font-medium text-foreground"
              >
                "{task.content}"
              </motion.p>
            )}
          </div>

          {/* Grace period countdown */}
          {countdown > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="flex items-center gap-2 text-sm text-muted-foreground"
            >
              <Clock className="h-4 w-4" />
              <span>Take a moment to wrap up... {countdown}s</span>
            </motion.div>
          )}

          {/* Question */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="space-y-4 w-full"
          >
            <p className="text-lg font-medium">
              {modeMessage.question}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="outline"
                size="lg"
                onClick={() => onComplete(false)}
                className="gap-2 min-w-[140px]"
              >
                <CheckCircle className="h-4 w-4" />
                {modeMessage.doneLabel}
              </Button>
              <Button
                variant="default"
                size="lg"
                onClick={() => onComplete(true)}
                className="gap-2 min-w-[140px]"
              >
                <Clock className="h-4 w-4" />
                {modeMessage.moreLabel}
              </Button>
            </div>
          </motion.div>

          {/* Skip to break hint - only for manager mode */}
          {timerMode === 'manager' && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="text-xs text-muted-foreground/60"
            >
              Either way, you've earned a break! <ArrowRight className="inline h-3 w-3" />
            </motion.p>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
